import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import BiddingAveragesModal from '../BiddingAveragesModal'
import RadioButton from '../../../../components/form/RadioButton'
import {
  BiddingStrategyTypes,
  getRankOptionSimpleText,
  getRankOptionText,
  MaxBiddingAmountChangeTypes,
  RankOptions,
} from '../../../Codes'
import SelectBox from '../../../../components/form/SelectBox'
import HelpTooltip from '../../../../components/tooltip/HelpTooltip'
import CurrencyInput from '../../../../components/form/CurrencyInput'
import Button from '../../../../components/form/Button'
import { integerValue, isEmptyObject } from '../../../../utils'
import CheckBox from '../../../../components/form/CheckBox'
import ErrorText from '../../../../components/form/ErrorText'
import PropTypes from 'prop-types'
import {
  fetchBiddingAverages,
  hideBiddingAverages,
  setBiddingStrategyType,
  setMaxBiddingAmount,
  setMaxBiddingAmountChangeType,
  setRank,
  setRankAmount,
  setUseRankAmount,
} from '../keywordSlice'
import { failWithErrorResponse } from '../../../toast/Toast'

const hasAcceptableError = errors =>
  (Object.keys(errors).length === 1 && errors.texts) || isEmptyObject(errors)
const BiddingStrategy = ({ errors, validate }) => {
  const dispatch = useDispatch()
  const {
    texts,
    suggestions,
    biddingStrategyType,
    maxBiddingAmount,
    maxBiddingAmountChangeType,
    rank,
    useRankAmount,
    rankAmount,
  } = useSelector(state => state.ad.keyword.form)
  const handleBiddingStrategyTypeChange = useCallback(
    value => dispatch(setBiddingStrategyType(value)),
    [dispatch]
  )
  const handleMaxBiddingAmountChangeTypeChange = useCallback(
    value => dispatch(setMaxBiddingAmountChangeType(value)),
    [dispatch]
  )
  const handleMaxBiddingAmountChange = useCallback(
    value => dispatch(setMaxBiddingAmount(integerValue(value))),
    [dispatch]
  )
  const handleRankChange = useCallback(
    value => dispatch(setRank(value)),
    [dispatch]
  )
  const handleUseRankAmountChange = useCallback(
    value => dispatch(setUseRankAmount(value)),
    [dispatch]
  )
  const handleRankAmountChange = useCallback(
    value => dispatch(setRankAmount(integerValue(value))),
    [dispatch]
  )
  const handlePreview = async () => {
    try {
      await validate()
    } catch (e) {
      if (!hasAcceptableError(e)) {
        return
      }
    }
    // noinspection JSCheckFunctionSignatures
    const { error, payload } = await dispatch(
      fetchBiddingAverages({
        keywords: [...texts, ...suggestions],
        rank,
        useRankAmount,
        rankAmount,
      })
    )
    if (error) {
      failWithErrorResponse(payload)
      dispatch(hideBiddingAverages())
    }
  }

  return (
    <>
      <div className="ad_managebox ad_option">
        <div className="area_item">
          <strong className="subtit_box">입찰단가</strong>
          <div className="cont_card">
            <div className="item_crad">
              <div className="filter_radio">
                <RadioButton
                  name="biddingStrategyType"
                  value={BiddingStrategyTypes.INHERIT_FROM_ADGROUP}
                  label="광고그룹 입찰"
                  tiaraAction="키워드등록_광고그룹입찰가_클릭"
                  checked={
                    biddingStrategyType ===
                    BiddingStrategyTypes.INHERIT_FROM_ADGROUP
                  }
                  onValueChange={handleBiddingStrategyTypeChange}
                />
                <RadioButton
                  name="biddingStrategyType"
                  value={BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING}
                  label="키워드 입찰"
                  tiaraAction="키워드등록_키워드입찰가_클릭"
                  checked={
                    biddingStrategyType ===
                    BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING
                  }
                  onValueChange={handleBiddingStrategyTypeChange}
                />
              </div>
              {biddingStrategyType ===
                BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING && (
                <div className="box_subcard">
                  <div className="area_inp">
                    <div className="single_wrap">
                      <SelectBox
                        noBlurOnDefault
                        className="opt_cate"
                        value={maxBiddingAmountChangeType}
                        onValueChange={handleMaxBiddingAmountChangeTypeChange}>
                        <SelectBox.Item
                          value={MaxBiddingAmountChangeTypes.NO_CALCULATE}
                          tiaraAction="키워드등록_키워드입찰가_직접입력_클릭">
                          직접입력
                        </SelectBox.Item>
                        <SelectBox.Item
                          value={MaxBiddingAmountChangeTypes.RANK}
                          tiaraAction="키워드등록_키워드입찰가_순위별평균_클릭">
                          순위별 평균 입찰가
                          <HelpTooltip nowrap={false} tag="span">
                            순위별 평균 입찰가는 프리미엄링크 캠페인 &gt;
                            검색매체 &gt; 카카오지면을 통해 광고가 집행된
                            키워드의 최근 30일(어제로부터 과거 30일까지) 과거
                            실적을 토대로 예측한 수치며 제공되는 데이터로 인한
                            광고 성과를 보장하지 않습니다. 순위별 평균 입찰가가
                            희망하는 입찰가를 초과하지 않도록 최대 입찰가를
                            설정할 수 있습니다.
                          </HelpTooltip>
                        </SelectBox.Item>
                      </SelectBox>
                    </div>
                    {maxBiddingAmountChangeType ===
                    MaxBiddingAmountChangeTypes.NO_CALCULATE ? (
                      <div className="single_wrap">
                        <CurrencyInput
                          name="maxBiddingAmount"
                          error={errors.maxBiddingAmount}
                          min={70}
                          max={100000}
                          step={10}
                          value={maxBiddingAmount}
                          onValueChange={handleMaxBiddingAmountChange}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="single_wrap">
                          <SelectBox
                            className="opt_ranking"
                            noBlurOnDefault
                            value={rank}
                            onValueChange={handleRankChange}>
                            {RankOptions.map(item => (
                              <SelectBox.Item
                                key={`${item.device}${item.rank}`}
                                value={item}
                                tiaraAction={`키워드등록_키워드입찰가_순위별평균_${getRankOptionSimpleText(
                                  item
                                )}위_클릭`}>
                                {getRankOptionText(item, false)}
                              </SelectBox.Item>
                            ))}
                          </SelectBox>
                        </div>
                        <div className="single_wrap">
                          <Button
                            line
                            disabled={
                              (texts.length === 0 &&
                                suggestions.length === 0) ||
                              !hasAcceptableError(errors)
                            }
                            onClick={handlePreview}>
                            미리보기
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                  {maxBiddingAmountChangeType ===
                  MaxBiddingAmountChangeTypes.RANK ? (
                    <div className="box_info">
                      <div className="inner_info">
                        <CheckBox
                          label="최대입찰가"
                          checked={useRankAmount}
                          onCheckedChange={handleUseRankAmountChange}
                        />
                        <CurrencyInput
                          name="rankAmount"
                          disabled={!useRankAmount}
                          error={errors.rankAmount}
                          min={70}
                          max={100000}
                          step={10}
                          value={rankAmount}
                          onValueChange={handleRankAmountChange}
                        />
                      </div>
                      <ErrorText tag="p" error={errors.rankAmount} />
                    </div>
                  ) : errors.maxBiddingAmount ? (
                    <p className="txt_error">{errors.maxBiddingAmount}</p>
                  ) : (
                    <p className="desc_item">
                      입찰단가는 최소 70원 ~ 최대 100,000원까지 10원 단위로
                      설정할 수 있습니다.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <BiddingAveragesModal />
    </>
  )
}
BiddingStrategy.propTypes = {
  errors: PropTypes.shape({
    rankAmount: PropTypes.string,
    maxBiddingAmount: PropTypes.string,
  }),
  validate: PropTypes.func,
}
export default BiddingStrategy
